import { FunctionComponent, useRef } from 'react'
import { Typography, Box, Avatar, Card } from '@material-ui/core'
import { useTheme } from 'styled-components'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import { CREATE_COMMENT_PLACEHOLDER } from 'common/types'
import { CustomIconContextMenu } from 'components/core/actions-menu-noborder/CustomIconContextMenu'
import { ConfirmModal, Modal } from 'components/core/modal'
import { ReportCheckboxList } from '../posts/report-post/ReportCheckboxList'
import { useHistory } from 'react-router-dom'
//@ts-ignore
import ReactTextFormat from 'react-text-format'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { htmlToReactParser } from 'common/HTMLToReactParser'
import { useMentions } from '../posts/hooks/useMentions'
import { MentionsTextfield } from '../posts/create-post/MentionsTextField'
import { MentionsSuggestionsList } from '../posts/create-post/MentionsSuggestionsList'
import { useReport } from '../posts/hooks/useReport'
import { DEV_ONLY } from 'common/globalConstants'
import { ReactComponent as ActionContextIcon } from 'icons/action-context-icon.svg'
import {
  StyledCommentBox,
  StyledDeleteIcon,
  StyledEditIcon,
  StyledHistoryIcon,
  StyledLargeDeleteIcon,
  StyledReportIcon
} from './styled'
import { CommentCardProps } from './types'
import { useManageComments } from './hooks/useManageComments'
import { useCommentsContextMenu } from './hooks/useCommentsContextMenu'
import { ContextMenuButton } from './components/ContextMenuButton'
import { Button } from 'common/atoms/Button/Button'

export const CommentCard: FunctionComponent<CommentCardProps> = ({
  comment,
  user,
  deleteComment,
  setComments,
  comments,
  setCommentCount,
  updateComment,
  reportContent,
  notify,
  post,
  getPost,
  backendUserFunctions
}) => {
  const theme = useTheme()
  const { backendUser } = useAuth()
  const {
    getResults,
    users,
    setUsers,
    setMentions,
    mentions,
    totalSuggestions,
    tags,
    setTags,
    setTagResults,
    tagResults,
    searchText
  } = useMentions()
  const commentResetMentioningRef = useRef(() => {})
  const history = useHistory()
  const { isDevice } = useScreenSizes()

  const {
    removeComment,
    handleEditComment,
    handleClick,
    handleClose,
    getName,
    commentCreatorLink,
    renderOption,
    checkForPartnerComment,
    setShowEdit,
    showEdit,
    notEmpty,
    notPassingMaxLength,
    editCommentPermission,
    deleteCommentPermission,
    maxCommentLength,
    editedCommentMentionStateDispatch,
    editedCommentMentionsState
  } = useManageComments({
    deleteComment,
    comments,
    setComments,
    setCommentCount,
    user,
    post,
    comment,
    updateComment,
    backendUserFunctions,
    mentions,
    tags
  })

  const { handleReport } = useReport(comment?.id)
  const canEditPost = notEmpty && notPassingMaxLength

  const isReported = () => {
    return comment?.reportedBy?.some(report => {
      if (report === user?.id) {
        return true
      }
      return false
    })
  }

  const {
    returnContextMenu,
    setShowDelete,
    setShowReportComment,
    showReportComment,
    showDelete
  } = useCommentsContextMenu({
    comment,
    user,
    post,
    isReported
  })

  const customLinkDecorator = (decoratedHref: string, decoratedText: string, key: number) => (
    <a href={decoratedHref} key={key} target="_blank" rel="noopener">
      {decoratedText}
    </a>
  )

  const linkToOwnProfile = (html: string) => {
    return html?.replaceAll(`/profile/${backendUser?.id}`, '/profile/') || html
  }

  const addLinks = (content: string) => linkToOwnProfile(content)
  const commentContent = addLinks(comment?.content[0]?.comment?.replaceAll('comment-val-mentioned', ''))

  return (
    <>
      <Box display="flex" width="100%" alignItems="flex-start" padding="0 1em" id={comment?.id}>
        <Box
          onClick={() => {
            commentCreatorLink()
          }}
          padding="1.5em 0 0 0"
          width={isDevice ? '15%' : '11%'}
          display="flex"
          justifyContent="center"
          style={{ cursor: 'pointer' }}
        >
          <Avatar
            src={comment?.partnerId && comment?.logo ? comment?.logo : comment?.image}
            alt="member with a profile"
            style={{ width: '2em', height: '2em' }}
          />
        </Box>
        <Box padding={isDevice ? '1em 0 1em 0.5em' : '0 0.5em 0.5em'} width="81%">
          <Card style={{ marginTop: '0.5em', padding: '0.5em' }}>
            <Box display="flex" flexDirection="row" justifyContent="space-between">
              <Box width={isDevice ? '65%' : '100%'} padding="0 0 0.5em 0" display="flex" flexDirection="column">
                <Typography variant="body2" align="left">
                  <Box
                    fontWeight="bold"
                    color={theme.colors.accent1}
                    onClick={() => commentCreatorLink()}
                    style={{ cursor: 'pointer' }}
                  >
                    {getName()}
                  </Box>
                </Typography>
                {!comment?.partnerId && (
                  <Box color={theme.colors.accent}>
                    <Typography gutterBottom variant="body2" component="span" style={{ wordWrap: 'break-word' }}>
                      <Box color={theme.colors.accent} display="inline">
                        {comment?.connectionTitle?.slice(0, 45)}
                      </Box>
                    </Typography>
                    {comment?.connectionTitle?.length > 45 && (
                      <Typography variant="subtitle2" style={{ display: 'contents' }}>
                        ...
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              {isDevice &&
              (comment?.userId === user?.id ||
                (backendUser?.partnerId &&
                  comment?.partnerId === user?.partnerId &&
                  (editCommentPermission || deleteCommentPermission))) ? (
                <CustomIconContextMenu
                  title=""
                  icon={<ActionContextIcon fill={theme.colors.accent1} />}
                  menuWidth="16em"
                  noPadding={true}
                >
                  {renderOption('edit') && (
                    <ContextMenuButton
                      icon={<StyledEditIcon />}
                      margin="1em 0"
                      text="Edit"
                      onClick={() => {
                        handleEditComment()
                      }}
                    />
                  )}
                  {renderOption('delete') && (
                    <ContextMenuButton
                      icon={<StyledDeleteIcon />}
                      margin="0.5em 0"
                      text=" Delete"
                      onClick={() => setShowDelete(true)}
                    />
                  )}
                  {comment?.partnerId && renderOption('log') && (
                    <ContextMenuButton
                      icon={<StyledHistoryIcon />}
                      margin="0.5em 0"
                      text=" Activity"
                      onClick={() => {
                        history.push(`/post/log/${comment?.id}`)
                      }}
                    />
                  )}

                  {comment?.userId !== user?.id && !checkForPartnerComment() && (
                    <ContextMenuButton
                      icon={<StyledReportIcon />}
                      text="Report Comment"
                      onClick={() => setShowReportComment(true)}
                      disabled={isReported()}
                    />
                  )}
                </CustomIconContextMenu>
              ) : (
                isDevice && comment?.userId !== user?.id && !checkForPartnerComment() && returnContextMenu()
              )}

              <ReportCheckboxList
                handleReport={handleReport}
                open={showReportComment}
                setShowReportModal={setShowReportComment}
                contentId={comment?.id}
                type={comment?.type}
                reportContent={reportContent}
                user={user}
                comment={comment}
                setComments={setComments}
                comments={comments}
                getPost={getPost}
                notify={notify}
                post={post}
                maxChars={100}
              />
            </Box>
            <Typography variant="body2" component="p" align="justify">
              <StyledCommentBox>
                <ReactTextFormat allowedFormats={['URL']} linkDecorator={customLinkDecorator}>
                  {htmlToReactParser.parse(commentContent)}
                </ReactTextFormat>
              </StyledCommentBox>
            </Typography>
          </Card>
        </Box>
        {!isDevice &&
        (comment?.userId === backendUser?.id ||
          (backendUser?.partnerId &&
            comment?.partnerId === backendUser?.partnerId &&
            (editCommentPermission || deleteCommentPermission))) ? (
          <CustomIconContextMenu title="" icon={<ActionContextIcon fill={theme.colors.accent1} />}>
            {renderOption('edit') && (
              <ContextMenuButton
                margin="1em 0"
                text="Edit"
                onClick={() => {
                  handleEditComment()
                }}
                icon={<StyledEditIcon />}
              />
            )}
            {renderOption('delete') && (
              <ContextMenuButton
                icon={<StyledDeleteIcon />}
                margin="0.5em 0"
                text="Delete"
                onClick={() => setShowDelete(true)}
              />
            )}
            {comment?.partnerId && renderOption('log') && (
              <ContextMenuButton
                icon={<StyledHistoryIcon />}
                margin="0.5em 0"
                text="Activity log"
                onClick={() => {
                  history.push(`/post/log/${comment?.id}`)
                }}
              />
            )}

            {comment?.userId !== user?.id && !checkForPartnerComment() && (
              <ContextMenuButton
                icon={<StyledReportIcon />}
                text="Report Comment"
                onClick={() => setShowReportComment(true)}
                disabled={isReported()}
              />
            )}
          </CustomIconContextMenu>
        ) : (
          !isDevice && comment?.userId !== user?.id && !checkForPartnerComment() && returnContextMenu()
        )}
      </Box>
      {comment?.userId !== user?.id && (
        <ConfirmModal
          title=""
          message="Are you sure you want to delete the comment?"
          onClose={() => setShowDelete(false)}
          open={showDelete}
          onConfirm={() => removeComment(comment?.userId, comment?.id, comment?.commentId)}
          icon={<StyledLargeDeleteIcon />}
        />
      )}
      {comment?.userId === user?.id && (
        <ConfirmModal
          title=""
          message="Are you sure you want to delete your comment?"
          onClose={() => setShowDelete(false)}
          open={showDelete}
          onConfirm={() => removeComment(user?.id, comment?.id, comment?.commentId)}
          icon={<StyledLargeDeleteIcon />}
        />
      )}
      <Modal
        title=""
        show={showEdit}
        onClose={() => {
          editedCommentMentionStateDispatch({
            textfieldValue: ''
          })
          setShowEdit(false)
        }}
        noBorder
        dialogWidth={isDevice ? 'sm' : 'lg'}
        overflow="visible"
      >
        <Box display="flex" flexDirection="column" width={isDevice ? '30em' : '38.75em'} gridGap="1em">
          <Box border={`1px solid ${theme.colors.accent1}`} padding="1rem" borderRadius="0.7rem" fontSize="1rem">
            <MentionsTextfield
              width="100%"
              getResults={getResults}
              maxLength={maxCommentLength}
              mentionedContainerClass="comment-val-mentioned"
              setMentions={setMentions}
              setUsers={setUsers}
              typingID={editedCommentMentionsState?.textfieldId}
              setCursorEl={(active: boolean) => {}}
              users={users}
              edit={false}
              theme={theme}
              setText={(val: string) => editedCommentMentionStateDispatch({ textfieldValue: val })}
              setCharacterCount={characterLength => {
                editedCommentMentionStateDispatch({ textfieldCharacterLength: characterLength })
              }}
              totalSuggestions={totalSuggestions}
              mentionedCounter={editedCommentMentionsState?.mentionedCounter}
              setMentionedCounter={newCounterValue =>
                editedCommentMentionStateDispatch({ mentionedCounter: newCounterValue })
              }
              resetMentioningRef={commentResetMentioningRef}
              isMentioning={editedCommentMentionsState?.isMentioning}
              setIsMentioning={(val: boolean) => editedCommentMentionStateDispatch({ isMentioning: val })}
              placeholder={CREATE_COMMENT_PLACEHOLDER}
              defaultValue={editedCommentMentionsState?.textfieldValue}
              fullWidth
              isTagging={editedCommentMentionsState?.isTagging}
              setIsTagging={isTagging => editedCommentMentionStateDispatch({ isTagging })}
              setTagResult={setTagResults}
              hasTagActive={DEV_ONLY}
              setHashCount={hashCount => editedCommentMentionStateDispatch({ hashCount })}
              hashCount={editedCommentMentionsState?.hashCount}
              mentions={mentions}
              tags={tags}
              state={editedCommentMentionsState}
              stateDispatch={editedCommentMentionStateDispatch}
            />
            <MentionsSuggestionsList
              value={editedCommentMentionsState?.textfieldValue}
              getResults={getResults}
              mentionedContainerClass="comment-val-mentioned"
              mentionedCounter={editedCommentMentionsState?.mentionedCounter}
              resetMentioningRef={commentResetMentioningRef}
              setMentions={setMentions}
              setText={(val: string) => editedCommentMentionStateDispatch({ textfieldValue: val })}
              setCharacterCount={characterLength => {
                editedCommentMentionStateDispatch({ textfieldCharacterLength: characterLength })
              }}
              totalSuggestions={totalSuggestions}
              typingID={editedCommentMentionsState?.textfieldId}
              users={users}
              isMentioning={editedCommentMentionsState?.isMentioning}
              setTagResults={setTagResults}
              isTagging={editedCommentMentionsState?.isTagging}
              tagResults={tagResults}
              searchText={searchText}
              setTags={setTags}
              hasTagActive={false}
              hashCount={editedCommentMentionsState?.hashCount}
            />
          </Box>
          <Box width="100%" display="flex" padding="0" justifyContent="flex-end" alignItems="center">
            <Box width="max-content" display="flex">
              <Box display="flex" alignItems="center" padding="0 0.5em">
                <Button design='primary' small onClick={handleClose}>
                  <Box fontWeight="700">
                    <Typography variant="button">Cancel</Typography>
                  </Box>
                </Button>
              </Box>
              <Box display="flex" alignItems="center">
                {/* @ts-ignore */}
                <Button design='primary' small disabled={!canEditPost} onClick={handleClick}>
                  <Box fontWeight="700">
                    <Typography variant="button">Save</Typography>
                  </Box>
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
